import React from 'react'
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { Check, PriorityHigh, FiberManualRecord } from '@mui/icons-material'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import { StyledList, StyledListVariants } from '../../types/heroTextSection'
import RichText from '../RichText'
import { BLOCKS } from '@contentful/rich-text-types'

interface StyleListInsertProps {
  list: StyledList
}

export const StyleListInsert: React.FC<StyleListInsertProps> = ({ list }) => {
  const { classes, cx } = useStyles()
  const { t } = useTranslation()

  let subheaderText = null
  if (list.variant === StyledListVariants.Challenge) {
    subheaderText = t('styledList.challenge')
  } else if (list.variant === StyledListVariants.KeyChanges) {
    subheaderText = t('styledList.keyChanges')
  }

  return (
    <List
      className={cx({
        [classes.list]: true,
        [classes.listChallenge]: list.variant === StyledListVariants.Challenge,
        [classes.listKeyChanges]:
          list.variant === StyledListVariants.KeyChanges,
      })}
      // @ts-ignore
      subheader={
        subheaderText && (
          <Typography
            className={classes.subheader}
            id="list-subheader"
            variant="overline"
          >
            {subheaderText}
          </Typography>
        )
      }
    >
      {list.listItems.map((item) => (
        <ListItem
          className={cx({
            [classes.listItem]: list.variant !== StyledListVariants.Bullet,
            [classes.listItemNoBorder]:
              list.variant === StyledListVariants.Bullet,
          })}
          disableGutters
          key={item.contentful_id}
        >
          <ListItemIcon className={classes.listItemIcon}>
            <StyleItemIcon variant={list.variant} />
          </ListItemIcon>
          <ListItemText
            className={cx({
              [classes.listItemText]:
                list.variant === StyledListVariants.Bullet,
            })}
            primaryTypographyProps={{ variant: 'body2' }}
          >
            <RichText
              rawBody={item.text.raw}
              options={{
                props: {
                  [BLOCKS.PARAGRAPH]: {
                    className: classes.text,
                  },
                },
              }}
            />
          </ListItemText>
        </ListItem>
      ))}
    </List>
  )
}

const StyleItemIcon: React.FC<{
  variant: StyledListVariants
}> = ({ variant }) => {
  const { classes, cx } = useStyles()

  switch (variant) {
    case StyledListVariants.Challenge:
      return (
        <PriorityHigh className={cx(classes.icon, classes.exclamationMark)} />
      )
    case StyledListVariants.Check:
      return <Check className={classes.icon} />
    case StyledListVariants.Bullet:
      return <FiberManualRecord className={classes.bullet} />
    default:
      return <Check className={classes.icon} />
  }
}

const useStyles = makeStyles()((theme) => ({
  exclamationMark: {
    color: theme.colors.orange.a100,
  },
  icon: {
    height: '1.25rem',
    marginRight: '.5rem',
    width: 'auto',
  },
  bullet: {
    color: 'black',
    height: '.7rem',
    marginRight: '.5rem',
    width: 'auto',
  },
  list: {
    columnGap: '0',
    columns: 2,
    [theme.breakpoints.down('lg')]: {
      columns: 'unset',
    },
  },
  listChallenge: {
    columns: 'unset',
  },
  listItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  listItemNoBorder: {
    paddingTop: '0px',
    paddingBottom: '0px',
  },
  listItemIcon: {
    minWidth: '1.67rem',
  },
  listKeyChanges: {
    columns: 'unset',
  },
  subheader: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'block',
    paddingBottom: '1em',
  },
  text: {
    [theme.breakpoints.down('md')]: {
      fontSize: '1rem',
    },
  },
  listItemText: {
    margin: 0,
  },
}))

export default StyleListInsert
